import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownContainer,
  DropdownMenu,
  DropdownOption,
  DropdownTrigger,
} from "./styles.roleDropdown";
import { getUserRoleConfig, USER_ROLE_OPTIONS } from "./userRoles";

const RoleDropdown = ({
  id,
  value,
  onChange,
  placeholder = "Select role",
  disabled = false,
}) => {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const [isOpen, setIsOpen] = useState(false);
  const selectedRole = USER_ROLE_OPTIONS.find((role) => role.value === value);
  const selectedRoleConfig = selectedRole || getUserRoleConfig(value);
  const menuId = `${id}-menu`;

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Move focus into the listbox on open (selected option, else the first),
    // fulfilling the listbox keyboard contract the ARIA roles advertise.
    const selectedIndex = USER_ROLE_OPTIONS.findIndex(
      (role) => role.value === value
    );
    optionRefs.current[selectedIndex > 0 ? selectedIndex : 0]?.focus();

    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen, value]);

  const handleSelect = (roleValue) => {
    onChange(roleValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event) => {
    const lastIndex = USER_ROLE_OPTIONS.length - 1;
    const currentIndex = optionRefs.current.findIndex(
      (node) => node === document.activeElement
    );

    if (event.key === "ArrowDown") {
      event.preventDefault();
      optionRefs.current[currentIndex < lastIndex ? currentIndex + 1 : 0]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      optionRefs.current[currentIndex > 0 ? currentIndex - 1 : lastIndex]?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      optionRefs.current[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      optionRefs.current[lastIndex]?.focus();
    }
  };

  const handleTriggerKeyDown = (event) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownTrigger
        id={id}
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={!disabled && isOpen}
        aria-controls={!disabled && isOpen ? menuId : undefined}
        $hasValue={Boolean(selectedRoleConfig)}
        disabled={disabled}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span>{selectedRoleConfig?.label || placeholder}</span>
        {!disabled && <ChevronDown aria-hidden="true" />}
      </DropdownTrigger>

      {isOpen && !disabled && (
        <DropdownMenu
          id={menuId}
          role="listbox"
          aria-labelledby={id}
          onKeyDown={handleMenuKeyDown}
        >
          {USER_ROLE_OPTIONS.map((role, index) => (
            <DropdownOption
              key={role.value}
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              type="button"
              role="option"
              aria-selected={role.value === value}
              $isSelected={role.value === value}
              onClick={() => handleSelect(role.value)}
            >
              {role.label}
            </DropdownOption>
          ))}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

export default RoleDropdown;
